/* eslint-disable @typescript-eslint/adjacent-overload-signatures */
import { Injectable } from '@angular/core';

import { BehaviorSubject, Subject } from 'rxjs';

type SelectionIdentifierKey<TItem> = Extract<keyof TItem, string>;

@Injectable()
export class SelectionManagerService<TItem extends Record<string, any> = any> {
  /**
   * general array for store selected items
   */
  private _selectedItems: TItem[] = [];

  /**
   * active selected item
   * (can be used to shown in form of the right part of the screen)
   */
  private _activeItem: TItem | undefined;

  /**
   * Events
   */
  private readonly _selectionChange = new Subject<void>();
  private readonly _deselectAll = new Subject<void>();
  private readonly _changeActiveItem = new Subject<void>();

  /**
   * function for checking can item be selected/deselected or not
   */
  public fnForChecking: (() => any) | null = null;

  private selectedTabSubject = new BehaviorSubject<number>(0);
  public selectedTab$ = this.selectedTabSubject.asObservable();

  constructor() {}

  public get selectedItems(): TItem[] {
    return this._selectedItems;
  }

  public get activeItem(): TItem | undefined {
    return this._activeItem;
  }

  public get deselectAll_$() {
    return this._deselectAll;
  }

  public set activeItem(item: TItem | undefined) {
    this._activeItem = item;
    this._changeActiveItem.next();
  }

  public setSelectedTab(tab: number) {
    this._activeItem = null;
    this.selectedTabSubject.next(tab);
  }

  public getSelectedTab() {
    return this.selectedTabSubject.value;
  }

  public selectionChange$(): Subject<void> {
    return this._selectionChange;
  }

  public changeActiveItem$(): Subject<void> {
    return this._changeActiveItem;
  }

  public deselectAll$(): Subject<void> {
    return this._deselectAll;
  }

  public updateItems(templates: TItem[]) {
    this._selectedItems = this._selectedItems.map((selectedItem) => {
      const updatedItem = templates.find((template) => template['id'] === selectedItem['id']);
      return updatedItem || selectedItem;
    });
    this._selectionChange.next();
  }

  /**
   *
   * Call this func for select/deselect with some rules/control
   * You can set checking fn to the `fnForChecking`
   * @param checked - checked flag
   * @param {any} item
   * @param negativeCaseCallback - if answer is false and we can't select/deselect the item
   */
  public itemSelectChangeWithChecking(
    checked: boolean,
    item: TItem,
    negativeCaseCallback: () => void,
  ) {
    if (!this.fnForChecking) {
      if (checked) {
        this.selectTemplate(item);
      } else {
        this.deselectTemplate(item);
      }
      this.setCurrentItemToLastElement();
      return;
    }

    const result$ = this.fnForChecking.call(null);

    result$.subscribe((response) => {
      if (response) {
        if (checked) {
          this.selectTemplate(item);
        } else {
          this.deselectTemplate(item);
        }
        this.setCurrentItemToLastElement();
      } else {
        negativeCaseCallback();
      }
    });
  }

  public selectTemplate(item: TItem, key?: SelectionIdentifierKey<TItem>) {
    const identifierKey = this.resolveIdentifierKey(item, key);
    const exists = this._selectedItems.some((tmpl) => tmpl[identifierKey] === item[identifierKey]);

    if (!exists) {
      this._selectedItems = [...this._selectedItems, item];
      this._selectionChange.next();
    }
  }

  public deselectTemplate(item: TItem, key?: SelectionIdentifierKey<TItem>) {
    const identifierKey = this.resolveIdentifierKey(item, key);
    const matchIndex = this._selectedItems.findIndex(
      (tmpl) => tmpl[identifierKey] === item[identifierKey],
    );
    if (matchIndex !== -1) {
      this._selectedItems = [
        ...this._selectedItems.slice(0, matchIndex),
        ...this._selectedItems.slice(matchIndex + 1),
      ];
      this._selectionChange.next();
    }
  }

  public setCurrentItemToLastElement() {
    const lastElem = this._selectedItems[this._selectedItems.length - 1];
    this.activeItem = lastElem;
  }

  public deselectAll() {
    this._selectedItems = this._selectedItems.map((it) => ({
      ...it,
      _selected: false,
    }));
    this._selectedItems = [];

    this._selectionChange.next();
    this._deselectAll.next();
    this.activeItem = undefined;
  }
  public deselectByCategory(
    items: Array<TItem & { _selected?: boolean }>,
    negativeCaseCallback: () => void,
  ) {
    if (!this.fnForChecking) {
      this._selectedItems.forEach((it) => this.deselectTemplate(it));
      this.setCurrentItemToLastElement();
      return;
    }

    const result$ = this.fnForChecking.call(null);

    const selectedItems = items.filter((template) => template._selected === true);
    const containsActiveItem = selectedItems.find(
      (template) => template['id'] === this._activeItem?.['id'],
    );

    if (containsActiveItem) {
      result$.subscribe((response) => {
        if (response) {
          this._selectedItems.forEach((it) => this.deselectTemplate(it));
          this.setCurrentItemToLastElement();
        } else {
          negativeCaseCallback();
        }
      });
    } else {
      this._selectedItems.forEach((it) => this.deselectTemplate(it));
      this.setCurrentItemToLastElement();
    }
  }

  private resolveIdentifierKey(
    item: TItem,
    key?: SelectionIdentifierKey<TItem>,
  ): SelectionIdentifierKey<TItem> {
    if (key) {
      return key;
    }

    if ('id' in item) {
      return 'id' as SelectionIdentifierKey<TItem>;
    }

    return 'remote_id' as SelectionIdentifierKey<TItem>;
  }
}
